import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from 'services/apiClient';

export const fetchCurrentUser = createAsyncThunk(
  'auth/fetchCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/auth/me');
      const payload = response?.data?.data ?? response?.data ?? {};
      const user = payload.user || payload.current_user || payload;
      return user;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch user');
    }
  }
);

export const loginWithMagicLink = createAsyncThunk(
  'auth/loginWithMagicLink',
  async ({ email }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/platform/auth/magic-link', { email });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to send magic link');
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await apiClient.post('/auth/session/revoke');
      const tokenManager = (await import('services/auth/tokenManager')).default;
      tokenManager.clearTokens();
      return null;
    } catch (error) {
      // Still clear tokens on error
      try {
        const tokenManager = (await import('services/auth/tokenManager')).default;
        tokenManager.clearTokens();
      } catch { /* ignore */ }
      return rejectWithValue(error.response?.data || 'Failed to logout');
    }
  }
);

export const refreshSession = createAsyncThunk(
  'auth/refreshSession',
  async (_, { rejectWithValue }) => {
    try {
      const tokenManager = (await import('services/auth/tokenManager')).default;
      const refreshToken = tokenManager.getRefreshToken();
      if (!refreshToken) return rejectWithValue('No refresh token');

      const response = await apiClient.post('/auth/session/refresh', {
        refresh_token: refreshToken,
      });

      if (response.data.access_token) {
        tokenManager.setToken(
          response.data.access_token,
          response.data.user,
          response.data.refresh_token
        );
        return response.data.user;
      }
      return rejectWithValue('No access token in response');
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Session refresh failed');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    isAuthenticated: false,
    isLoading: false,
    role: null,
    organizationId: null,
    permissions: [],
    error: null,
  },
  reducers: {
    setUserFromToken: (state) => {
      try {
        const raw = localStorage.getItem('platform_user');
        if (raw) {
          const user = JSON.parse(raw);
          state.user = user;
          state.isAuthenticated = true;
          state.role = user.role || null;
          state.organizationId = user.organization_id || null;
          state.permissions = user.permissions || [];
        }
      } catch {
        // ignore parse errors
      }
    },
    clearAuth: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.role = null;
      state.organizationId = null;
      state.permissions = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCurrentUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.role = action.payload?.role || null;
        state.organizationId = action.payload?.organization_id || null;
        state.permissions = action.payload?.permissions || [];
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.role = null;
        state.organizationId = null;
        state.permissions = [];
      });
  },
});

export const { setUserFromToken, clearAuth } = authSlice.actions;
export default authSlice.reducer;
